import React, { useDeferredValue, useEffect, useMemo, useState, } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpenCheck, Building2, Filter, Loader2, Mail, RefreshCw, RotateCcw, Search, ShieldCheck, ShieldOff, Trash2, Users, X, } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import SecretCodeModal from "../components/SecretCodeModal";
const API_URL = window.location.hostname === "localhost"
    ? "http://localhost:3001"
    : "https://api.certilink.tn";
const getToken = () => {
    const directToken = localStorage.getItem("token");
    if (directToken)
        return directToken;
    try {
        return JSON.parse(localStorage.getItem("userInfo") || "null")?.token || null;
    }
    catch {
        return null;
    }
};
const decodeTokenPayload = (token) => {
    try {
        const base64Url = token.split(".")[1];
        if (!base64Url)
            return null;
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        return JSON.parse(window.atob(base64));
    }
    catch {
        return null;
    }
};
const isAdminSession = (token) => {
    let storedUser = null;
    let storedUserInfo = null;
    try {
        storedUser = JSON.parse(localStorage.getItem("user") || "null");
    }
    catch {
        storedUser = null;
    }
    try {
        storedUserInfo = JSON.parse(localStorage.getItem("userInfo") || "null");
    }
    catch {
        storedUserInfo = null;
    }
    const payload = decodeTokenPayload(token) || {};
    const role = storedUser?.type ||
        storedUser?.role ||
        storedUserInfo?.type ||
        storedUserInfo?.role ||
        payload.type ||
        payload.role ||
        "";
    return (String(role).toLowerCase() === "admin" ||
        Boolean(storedUser?.isAdmin) ||
        Boolean(storedUserInfo?.isAdmin) ||
        Boolean(payload.isAdmin));
};
const getParticipantsCount = (client) => {
    const count = Number(client?.participantsCount);
    if (Number.isFinite(count))
        return count;
    if (Array.isArray(client?.participants))
        return client.participants.length;
    return 0;
};
const getInitials = (name) => String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase() || "?";
const Clients = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [error, setError] = useState("");
    const [updatingThemeAccessId, setUpdatingThemeAccessId] = useState("");
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deletionCode, setDeletionCode] = useState("");
    const [deletingClient, setDeletingClient] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const deferredSearchTerm = useDeferredValue(searchTerm);
    const [showFilters, setShowFilters] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all");
    const [participantFilter, setParticipantFilter] = useState("all");
    const [sortBy, setSortBy] = useState("name-asc");
    const tr = (key, fallback) => t?.ui?.clients?.[key] || t?.[key] || fallback;
    const fetchClients = async (token) => {
        setLoading(true);
        setError("");
        try {
            const response = await fetch(`${API_URL}/api/clients`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.message || tr("loadClientsError", "Chargement impossible"));
            }
            setClients(Array.isArray(data) ? data : []);
        }
        catch (fetchError) {
            console.error("Clients fetch error:", fetchError);
            setError(fetchError.message || tr("loadClientsError", "Chargement impossible"));
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        const token = getToken();
        if (!token) {
            navigate("/login", { replace: true });
            return;
        }
        if (!isAdminSession(token)) {
            navigate("/", { replace: true });
            return;
        }
        setCheckingAuth(false);
        fetchClients(token);
    }, [navigate]);
    const filteredClients = useMemo(() => {
        const query = deferredSearchTerm.trim().toLocaleLowerCase("fr");
        const result = clients.filter((client) => {
            const participantsCount = getParticipantsCount(client);
            const searchableText = [client.name, client.email, client.status]
                .filter(Boolean)
                .join(" ")
                .toLocaleLowerCase("fr");
            return ((!query || searchableText.includes(query)) &&
                (statusFilter === "all" || client.status === statusFilter) &&
                (participantFilter === "all" ||
                    (participantFilter === "with" && participantsCount > 0) ||
                    (participantFilter === "without" && participantsCount === 0)));
        });
        return [...result].sort((a, b) => {
            if (sortBy === "name-desc") {
                return String(b.name || "").localeCompare(String(a.name || ""), "fr");
            }
            if (sortBy === "participants-desc") {
                return getParticipantsCount(b) - getParticipantsCount(a);
            }
            if (sortBy === "participants-asc") {
                return getParticipantsCount(a) - getParticipantsCount(b);
            }
            if (sortBy === "status") {
                return String(a.status || "").localeCompare(String(b.status || ""), "fr");
            }
            return String(a.name || "").localeCompare(String(b.name || ""), "fr");
        });
    }, [clients, deferredSearchTerm, participantFilter, sortBy, statusFilter]);
    const activeFilterCount = [
        statusFilter !== "all",
        participantFilter !== "all",
    ].filter(Boolean).length;
    const totalParticipants = useMemo(() => clients.reduce((sum, client) => sum + getParticipantsCount(client), 0), [clients]);
    const resetFilters = () => {
        setSearchTerm("");
        setStatusFilter("all");
        setParticipantFilter("all");
        setSortBy("name-asc");
    };
    const handleToggleStatus = async (client, event) => {
        event.stopPropagation();
        const nextStatus = client.status === "Active" ? tr("inactive", "Inactive") : tr("active", "Active");
        const action = nextStatus === "Active" ? tr("activate", "Activer") : tr("deactivate", "Désactiver");
        if (!window.confirm(tr("confirmAction", "Voulez-vous {action} le compte de {name} ?").replace("{action}", action.toLowerCase()).replace("{name}", client.name))) {
            return;
        }
        try {
            const token = getToken();
            const response = await fetch(`${API_URL}/api/clients/${client._id}/status`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.message || tr("updateFailed", "Modification impossible"));
            }
            setClients((current) => current.map((item) => item._id === client._id
                ? { ...item, status: data.status || nextStatus }
                : item));
        }
        catch (updateError) {
            alert(`${tr("updateFailed", "Modification impossible")}: ${updateError.message}`);
        }
    };
    const handleToggleThemeAccess = async (client, event) => {
        event.stopPropagation();
        const enabled = !Boolean(client.canManageThemes);
        const confirmation = tr(enabled ? "confirmEnableThemeAccess" : "confirmDisableThemeAccess", enabled
            ? "Autoriser {name} à gérer le catalogue global des thèmes ?"
            : "Retirer à {name} l’accès à la gestion des thèmes ?").replace("{name}", client.name || tr("client", "Client"));
        if (!window.confirm(confirmation))
            return;
        setUpdatingThemeAccessId(client._id);
        try {
            const token = getToken();
            const response = await fetch(`${API_URL}/api/clients/${client._id}/theme-management-access`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ enabled }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.message || tr("themeAccessUpdateFailed", "Modification de l’accès impossible"));
            }
            setClients((current) => current.map((item) => item._id === client._id
                ? { ...item, canManageThemes: Boolean(data.canManageThemes) }
                : item));
        }
        catch (updateError) {
            alert(`${tr("themeAccessUpdateFailed", "Modification de l’accès impossible")}: ${updateError.message}`);
        }
        finally {
            setUpdatingThemeAccessId("");
        }
    };
    const openDeleteClient = (client, event) => {
        event?.stopPropagation();
        setDeleteTarget(client);
        setDeletionCode("");
    };
    const confirmDeleteClient = async () => {
        if (!deleteTarget || !deletionCode.trim())
            return;
        setDeletingClient(true);
        try {
            const token = getToken();
            const response = await fetch(`${API_URL}/api/clients/${deleteTarget._id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ secretCode: deletionCode }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.message || tr("deleteError", "Suppression impossible"));
            }
            setClients((current) => current.filter((item) => item._id !== deleteTarget._id));
            setDeleteTarget(null);
            setDeletionCode("");
        }
        catch (deleteError) {
            alert(`${tr("deleteError", "Suppression impossible")}: ${deleteError.message}`);
        }
        finally {
            setDeletingClient(false);
        }
    };
    if (checkingAuth || loading) {
        return (<div className="flex min-h-[60vh] items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-sm font-semibold text-slate-600">
          <RefreshCw className="h-5 w-5 animate-spin text-blue-600"/>
          {checkingAuth
                ? tr("checkingPermissions", "Vérification des autorisations…")
                : tr("loadingClients", "Chargement des clients…")}
        </div>
      </div>);
    }
    return (<div className="min-h-screen bg-slate-50 px-3 pb-8 pt-20 sm:px-5">
      <div className="mx-auto max-w-7xl">
        <div className="relative mb-5 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 px-5 py-6 text-white shadow-xl shadow-slate-200/70 sm:px-7">
          <div className="absolute -right-12 -top-16 h-44 w-44 rounded-full bg-blue-500/20 blur-2xl"/>
          <div className="absolute -bottom-20 left-1/3 h-40 w-40 rounded-full bg-cyan-400/10 blur-2xl"/>
          <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/15">
                <Building2 className="h-6 w-6 text-cyan-300"/>
              </span>
              <div>
                <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                  {tr("clientsManagement", "Gestion des clients")}
                </h1>
                <p className="mt-1 text-sm text-slate-300">
                  {tr("subtitle", "Cliquez sur un client pour consulter ses participants.")}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur">
                <div className="text-2xl font-bold text-white">{filteredClients.length}</div>
                <div className="text-xs font-medium uppercase tracking-wider text-slate-300">
                  {clients.length} {tr("clientsCount", "clients")}
                </div>
              </div>
              <div className="hidden rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur sm:block">
                <div className="text-2xl font-bold text-cyan-200">{totalParticipants}</div>
                <div className="text-xs font-medium uppercase tracking-wider text-slate-300">{tr("participants", "Participants")}</div>
              </div>
            </div>
          </div>
        </div>

        {error ? (<div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700 shadow-sm">
            {error}
          </div>) : null}

        <div className="mb-4 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm shadow-slate-200/60">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"/>
              <input type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder={tr("searchPlaceholder", "Rechercher un client par nom ou email\u2026")} className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-10 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100/60"/>
              {searchTerm ? (<button type="button" aria-label={tr("clearSearch", "Effacer la recherche")} onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition hover:text-slate-700">
                  <X className="h-4 w-4"/>
                </button>) : null}
            </div>

            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)} className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-400 focus:ring-4 focus:ring-blue-100/60">
              <option value="name-asc">{tr("sortNameAsc", "Nom A\u2013Z")}</option>
              <option value="name-desc">{tr("sortNameDesc", "Nom Z\u2013A")}</option>
              <option value="participants-desc">{tr("sortParticipantsDesc", "Plus de participants")}</option>
              <option value="participants-asc">{tr("sortParticipantsAsc", "Moins de participants")}</option>
              <option value="status">{tr("sortStatus", "Statut")}</option>
            </select>

            <button type="button" onClick={() => setShowFilters((current) => !current)} className={`inline-flex h-11 items-center justify-center gap-2 rounded-xl border px-4 text-sm font-semibold transition ${showFilters || activeFilterCount > 0
            ? "border-blue-200 bg-blue-50 text-blue-700"
            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
              <Filter className="h-4 w-4"/>{tr("filters", "Filtres")}{activeFilterCount > 0 ? (<span className="rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] text-white">
                  {activeFilterCount}
                </span>) : null}
            </button>

            {(activeFilterCount > 0 || searchTerm) && (<button type="button" title={tr("reset", "R\u00E9initialiser")} onClick={resetFilters} className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600">
                <RotateCcw className="h-4 w-4"/>
              </button>)}
          </div>

          {showFilters ? (<div className="mt-3 grid grid-cols-1 gap-2 border-t border-slate-100 pt-3 sm:grid-cols-2">
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 outline-none focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100/60">
                <option value="all">{tr("allStatuses", "Tous les statuts")}</option>
                <option value="Active">{tr("active", "Actifs")}</option>
                <option value="Inactive">{tr("inactive", "Inactifs")}</option>
              </select>

              <select value={participantFilter} onChange={(event) => setParticipantFilter(event.target.value)} className="h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700 outline-none focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100/60">
                <option value="all">{tr("allClients", "Tous les clients")}</option>
                <option value="with">{tr("withParticipants", "Avec participants")}</option>
                <option value="without">{tr("withoutParticipant", "Sans participant")}</option>
              </select>
            </div>) : null}
        </div>

        {filteredClients.length === 0 ? (<div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center shadow-sm">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50">
              <Building2 className="h-7 w-7 text-blue-600"/>
            </span>
            <h2 className="mt-4 text-lg font-bold text-slate-900">{tr("noClient", "Aucun client trouv\u00E9")}</h2>
            <p className="mt-1 text-sm text-slate-500">{tr("adjustSearch", "Modifiez la recherche ou r\u00E9initialisez les filtres.")}</p>
          </div>) : (<div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-200/50">
            <div className="hidden max-h-[68vh] overflow-auto md:block">
              <table className="w-full table-fixed text-left text-sm">
                <colgroup>
                  <col className="w-[27%]"/>
                  <col className="w-[25%]"/>
                  <col className="w-[14%]"/>
                  <col className="w-[14%]"/>
                  <col className="w-[16%]"/>
                  <col className="w-[8%]"/>
                </colgroup>
                <thead className="sticky top-0 z-10 bg-slate-900 text-xs uppercase tracking-wider text-slate-300 shadow-sm">
                  <tr>
                    <th className="px-4 py-3 font-semibold">{tr("client", "Client")}</th>
                    <th className="px-4 py-3 font-semibold">{tr("contact", "Contact")}</th>
                    <th className="px-4 py-3 text-center font-semibold">{tr("participants", "Participants")}</th>
                    <th className="px-4 py-3 text-center font-semibold">{tr("sortStatus", "Statut")}</th>
                    <th className="px-4 py-3 text-center font-semibold">{tr("themeManagementAccess", "Gestion des thèmes")}</th>
                    <th className="px-4 py-3 text-center font-semibold">{tr("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredClients.map((client) => {
                const participantsCount = getParticipantsCount(client);
                const isActive = client.status === "Active";
                const hasThemeAccess = Boolean(client.canManageThemes);
                return (<tr key={client._id} role="link" tabIndex={0} onClick={() => navigate(`/participants?client_id=${client._id}`)} onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            navigate(`/participants?client_id=${client._id}`);
                        }
                    }} className="cursor-pointer transition hover:bg-blue-50/80 focus:bg-blue-50/80 focus:outline-none">
                        <td className="px-4 py-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 text-xs font-bold text-white shadow-sm">
                              {getInitials(client.name)}
                            </span>
                            <div className="min-w-0">
                              <div className="truncate font-semibold text-slate-900" title={client.name || ""}>
                                {client.name || tr("noName", "Sans nom")}
                              </div>
                              <div className="mt-0.5 truncate text-xs text-slate-500">{tr("idPrefix", "ID \u00B7")}{String(client._id || "").slice(-8)}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex min-w-0 items-center gap-2 text-slate-700">
                            <Mail className="h-3.5 w-3.5 shrink-0 text-slate-400"/>
                            <span className="truncate" title={client.email || ""}>
                              {client.email || "—"}
                            </span>
                          </div>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-blue-700 ring-1 ring-inset ring-blue-200">
                            <Users className="h-3.5 w-3.5"/>
                            {participantsCount}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button type="button" onClick={(event) => handleToggleStatus(client, event)} title={isActive ? tr("deactivateClient", "D\u00E9sactiver le client") : tr("activateClient", "Activer le client")} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ring-1 ring-inset transition ${isActive
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100"
                        : "bg-rose-50 text-rose-700 ring-rose-200 hover:bg-rose-100"}`}>
                            {isActive ? (<ShieldCheck className="h-3.5 w-3.5"/>) : (<ShieldOff className="h-3.5 w-3.5"/>)}
                            {isActive ? tr("activeSingle", "Actif") : tr("inactiveSingle", "Inactif")}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button type="button" disabled={updatingThemeAccessId === client._id} onClick={(event) => handleToggleThemeAccess(client, event)} title={hasThemeAccess ? tr("disableThemeAccess", "Retirer l’accès") : tr("enableThemeAccess", "Autoriser l’accès")} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ring-1 ring-inset transition disabled:cursor-wait disabled:opacity-60 ${hasThemeAccess
                        ? "bg-violet-50 text-violet-700 ring-violet-200 hover:bg-violet-100"
                        : "bg-slate-100 text-slate-600 ring-slate-200 hover:bg-blue-50 hover:text-blue-700"}`}>
                            {updatingThemeAccessId === client._id ? (<Loader2 className="h-3.5 w-3.5 animate-spin"/>) : (<BookOpenCheck className="h-3.5 w-3.5"/>)}
                            {hasThemeAccess ? tr("themeAccessAllowed", "Autorisé") : tr("themeAccessDenied", "Non autorisé")}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button type="button" onClick={(event) => openDeleteClient(client, event)} title={tr("delete", "Supprimer")} className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-rose-200 bg-white text-rose-600 transition hover:bg-rose-50">
                            <Trash2 className="h-4 w-4"/>
                          </button>
                        </td>
                      </tr>);
            })}
                </tbody>
              </table>
            </div>

            <div className="divide-y divide-slate-100 md:hidden">
              {filteredClients.map((client) => {
                const participantsCount = getParticipantsCount(client);
                const isActive = client.status === "Active";
                const hasThemeAccess = Boolean(client.canManageThemes);
                return (<article key={client._id} role="link" tabIndex={0} onClick={() => navigate(`/participants?client_id=${client._id}`)} onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            navigate(`/participants?client_id=${client._id}`);
                        }
                    }} className="cursor-pointer p-4 transition hover:bg-blue-50 focus:bg-blue-50 focus:outline-none">
                    <div className="flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 text-xs font-bold text-white">
                        {getInitials(client.name)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate font-bold text-slate-900">
                              {client.name || tr("noName", "Sans nom")}
                            </div>
                            <div className="mt-1 truncate text-sm text-slate-600">
                              {client.email || "—"}
                            </div>
                          </div>
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-blue-50 px-2 py-1 text-xs font-bold text-blue-700">
                            <Users className="h-3.5 w-3.5"/>
                            {participantsCount}
                          </span>
                        </div>

                        <div className="mt-3 flex flex-wrap gap-2">
                          <button type="button" onClick={(event) => handleToggleStatus(client, event)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ring-1 ring-inset ${isActive
                        ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                        : "bg-rose-50 text-rose-700 ring-rose-200"}`}>
                            {isActive ? (<ShieldCheck className="h-3.5 w-3.5"/>) : (<ShieldOff className="h-3.5 w-3.5"/>)}
                            {isActive ? tr("activeSingle", "Actif") : tr("inactiveSingle", "Inactif")}
                          </button>
                          <button type="button" disabled={updatingThemeAccessId === client._id} onClick={(event) => handleToggleThemeAccess(client, event)} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold ring-1 ring-inset disabled:cursor-wait disabled:opacity-60 ${hasThemeAccess
                        ? "bg-violet-50 text-violet-700 ring-violet-200"
                        : "bg-slate-100 text-slate-600 ring-slate-200"}`}>
                            {updatingThemeAccessId === client._id ? (<Loader2 className="h-3.5 w-3.5 animate-spin"/>) : (<BookOpenCheck className="h-3.5 w-3.5"/>)}
                            {hasThemeAccess ? tr("themeAccessAllowed", "Thèmes autorisés") : tr("themeAccessDenied", "Thèmes non autorisés")}
                          </button>
                          <button type="button" onClick={(event) => openDeleteClient(client, event)} className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-700 ring-1 ring-inset ring-rose-200">
                            <Trash2 className="h-3.5 w-3.5"/>
                            {tr("delete", "Supprimer")}
                          </button>
                        </div>
                      </div>
                    </div>
                  </article>);
            })}
            </div>
          </div>)}
      </div>
      <SecretCodeModal
        open={Boolean(deleteTarget)}
        title={tr("deleteClientTitle", "Supprimer ce client")}
        description={tr("deleteClientDescription", `Le client « ${deleteTarget?.name || ""} » et les données supprimées par le comportement actuel seront concernés. Saisissez votre code de suppression personnel.`)}
        code={deletionCode}
        setCode={setDeletionCode}
        onClose={() => {
            if (!deletingClient) {
                setDeleteTarget(null);
                setDeletionCode("");
            }
        }}
        onConfirm={confirmDeleteClient}
        loading={deletingClient}
        labels={{
            protectedDeletion: tr("protectedDeletion", "Suppression protégée"),
            codeLabel: tr("deletionCodeLabel", "Code de suppression de l’administrateur"),
            codePlaceholder: tr("deletionCodePlaceholder", "Saisissez votre code de suppression"),
            cancel: tr("cancel", "Annuler"),
            confirm: tr("confirmDelete", "Confirmer la suppression"),
            deleting: tr("deleting", "Suppression…"),
            close: tr("close", "Fermer"),
        }}
      />
    </div>);
};
export default Clients;
